import { MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AppointmentCalendarItem, AppointmentAccent } from "./types";
import { formatCompactTimeRange, getAppointmentDurationHours } from "./utils";

interface AppointmentSlotCardProps {
  appointment: AppointmentCalendarItem;
  accent: AppointmentAccent;
  onClick: (appointmentId: string) => void;
}

export const AppointmentSlotCard = ({
  appointment,
  accent,
  onClick,
}: AppointmentSlotCardProps) => {
  const duration = getAppointmentDurationHours(
    appointment.start_time,
    appointment.end_time
  );
  const isCompact = duration < 0.9;
  const minHeight = Math.max(duration, 0.5) * 36;

  return (
    <button
      type="button"
      onClick={() => onClick(appointment.id)}
      className={cn(
        "flex w-full flex-col items-start overflow-hidden rounded-md border px-1.5 py-1 text-left shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        isCompact ? "gap-0.5" : "gap-1",
        accent.cardClassName
      )}
      style={{ minHeight: `${minHeight}px` }}
    >
      <div className="flex w-full items-center gap-1">
        <span
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full",
            accent.dotClassName
          )}
        />
        <span className="truncate z-caption leading-tight text-foreground">
          {appointment.staffName}
        </span>
        {appointment.clinic && (
          <span
            className="ml-auto shrink-0"
            title={`In-person at ${appointment.clinic.name}`}
          >
            <MapPin
              className="h-2.5 w-2.5 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="sr-only">
              {`In-person at ${appointment.clinic.name}`}
            </span>
          </span>
        )}
      </div>

      <div className="z-caption z-numeric whitespace-nowrap leading-tight text-muted-foreground">
        {formatCompactTimeRange(appointment.start_time, appointment.end_time)}
      </div>

      {duration >= 0.9 ? (
        <div className="w-full truncate z-caption leading-tight text-foreground/85">
          <span className="block truncate">{appointment.service_name}</span>
        </div>
      ) : null}
    </button>
  );
};
